import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ArrowLeft, Car, FileText, Download, Send, Loader2, User, Calendar,
  CheckCircle2, AlertTriangle, Phone, Trash2, ArrowRightCircle,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PhotosCorners, { PhotoCornerSlot } from "@/components/PhotosCorners";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FUEL_LEVELS = ["vide", "1/4", "1/2", "3/4", "plein"];
const STATUT_COLORS = {
  actif: "bg-blue-50 text-blue-700 border-blue-200",
  cloture: "bg-[#52a675]/15 text-[#3d8a5c] border-[#52a675]/40",
  annule: "bg-gray-100 text-gray-500 border-gray-200",
};
const STATUT_LABELS = { actif: "Actif", cloture: "Clôturé", annule: "Annulé" };

function nowLocalIso() {
  const d = new Date();
  return new Date(d - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

function fetchPdf(url, filename, inline = true) {
  const token = localStorage.getItem("cm_token");
  return fetch(`${BACKEND_URL}${url}`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => { if (!r.ok) throw new Error("Erreur PDF"); return r.blob(); })
    .then((blob) => {
      const objUrl = URL.createObjectURL(blob);
      if (inline) {
        window.open(objUrl, "_blank", "noopener");
      } else {
        const a = document.createElement("a");
        a.href = objUrl; a.download = filename; a.click();
      }
      setTimeout(() => URL.revokeObjectURL(objUrl), 60000);
    })
    .catch(() => toast.error("Erreur"));
}

export default function PretDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pret, setPret] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRetour, setShowRetour] = useState(false);
  const [sendingSms, setSendingSms] = useState(false);

  const [retour, setRetour] = useState({
    date_heure: nowLocalIso(),
    km_retour: "",
    niveau_carburant: "plein",
    etat_final: "",
    nouveaux_dommages: "",
    photos: {},
    supplements: 0,
    supplements_motif: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const fetch1 = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/prets/${id}`);
      setPret(data);
    } catch (e) {
      toast.error("Prêt introuvable");
      navigate("/remplacement");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetch1(); }, [id]); // eslint-disable-line

  const sendSms = async () => {
    setSendingSms(true);
    try {
      const { data } = await api.post(`/prets/${id}/send-sms`);
      toast.success(data.sms?.status === "mocked" ? "Lien généré (SMS mocké)" : `SMS envoyé au ${data.to}`);
      navigator.clipboard?.writeText(data.url).catch(() => {});
      fetch1();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSendingSms(false); }
  };

  const submitRetour = async () => {
    const hasKmRetour = retour.km_retour !== "" && !isNaN(parseInt(retour.km_retour, 10));
    const km_ret = hasKmRetour ? parseInt(retour.km_retour, 10) : null;
    if (km_ret !== null && km_ret < (pret?.depose?.km_depart || 0)) {
      toast.error("Le km de retour doit être ≥ au km de départ");
      return;
    }
    setSubmitting(true);
    try {
      const photosArr = [];
      ["av_g", "av_d", "ar_g", "ar_d"].forEach((k) => { if (retour.photos[k]) photosArr.push(retour.photos[k]); });
      const payload = {
        date_heure: new Date(retour.date_heure).toISOString(),
        km_retour: km_ret,
        niveau_carburant: retour.niveau_carburant,
        etat_final: retour.etat_final || null,
        nouveaux_dommages: retour.nouveaux_dommages || null,
        photos: photosArr,
        supplements: parseFloat(retour.supplements || 0),
        supplements_motif: retour.supplements_motif || null,
      };
      const { data } = await api.post(`/prets/${id}/retour`, payload);
      toast.success(`Prêt clôturé · ${data.duree_jours} j · ${data.total_facture.toFixed(2)} CHF`);
      setShowRetour(false);
      fetch1();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSubmitting(false); }
  };

  const removePret = async () => {
    if (!window.confirm("Supprimer définitivement ce prêt ?")) return;
    try {
      await api.delete(`/prets/${id}`);
      toast.success("Supprimé");
      navigate("/remplacement");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (loading || !pret) return <div className="p-8 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  const dep = pret.depose || {};
  const cond = dep.conducteur || {};
  const ret = pret.retour;
  const isActif = pret.statut === "actif";
  const canClose = isActif && (user?.role === "admin" || user?.role === "comptoir" || user?.role === "atelier");

  return (
    <div className="p-4 lg:p-8 max-w-5xl mx-auto" data-testid="pret-detail-page">
      <button onClick={() => navigate("/remplacement")} className="text-sm text-gray-500 hover:text-gray-900 inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-4 h-4" /> Retour
      </button>

      <Card className="p-5 border-gray-200 mb-4">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-14 h-14 rounded-lg bg-[#52a675]/10 text-[#52a675] flex items-center justify-center">
            <Car className="w-7 h-7" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <code className="text-2xl font-bold font-mono text-gray-900" data-testid="pret-numero">{pret.numero}</code>
              <Badge variant="outline" className={STATUT_COLORS[pret.statut]}>{STATUT_LABELS[pret.statut]}</Badge>
              {dep.courtoisie && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Courtoisie</Badge>}
            </div>
            <div className="text-sm text-gray-600 flex items-center gap-1.5 mt-1">
              <User className="w-3.5 h-3.5 text-gray-400" /> <span className="font-semibold">{cond.prenom} {cond.nom}</span>
              <span className="text-gray-400 mx-1">·</span>
              <Phone className="w-3.5 h-3.5 text-gray-400" /> {cond.tel}
            </div>
            <div className="text-sm text-gray-600 mt-0.5">{pret.flotte_label}</div>
          </div>
          {ret?.total_facture > 0 && (
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-gray-500">Total facturé</div>
              <div className="text-2xl font-bold font-mono text-gray-900">{ret.total_facture.toFixed(2)}</div>
              <div className="text-xs text-gray-500">CHF</div>
            </div>
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => fetchPdf(`/api/prets/${id}/contrat/pdf`, `contrat-${pret.numero}.pdf`)} data-testid="pret-contrat-pdf">
            <FileText className="w-3.5 h-3.5 mr-1.5" /> Contrat PDF
          </Button>
          {ret && (
            <Button size="sm" variant="outline" onClick={() => fetchPdf(`/api/prets/${id}/retour/pdf`, `retour-${pret.numero}.pdf`)} data-testid="pret-retour-pdf">
              <FileText className="w-3.5 h-3.5 mr-1.5" /> Bordereau retour
            </Button>
          )}
          {isActif && (
            <Button size="sm" onClick={sendSms} disabled={sendingSms} className="bg-[#0891b2] hover:bg-[#0e7490]" data-testid="pret-send-sms">
              {sendingSms ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
              Envoyer SMS au conducteur
            </Button>
          )}
          {canClose && !showRetour && (
            <Button size="sm" onClick={() => setShowRetour(true)} className="bg-[#52a675] hover:bg-[#3d8a5c] ml-auto" data-testid="pret-open-retour">
              <ArrowRightCircle className="w-4 h-4 mr-1.5" /> Clôturer (retour véhicule)
            </Button>
          )}
        </div>
      </Card>

      {showRetour && (
        <Card className="p-5 border-[#52a675]/40 bg-[#f5faf7] mb-4" data-testid="pret-retour-form">
          <h3 className="font-heading font-bold text-lg mb-3 text-[#2d5f3f]">État des lieux au retour</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Date / heure retour *</Label>
              <Input type="datetime-local" value={retour.date_heure} onChange={(e) => setRetour({ ...retour, date_heure: e.target.value })} data-testid="retour-date" />
            </div>
            <div>
              <Label>Km retour</Label>
              <Input type="number" value={retour.km_retour} onChange={(e) => setRetour({ ...retour, km_retour: e.target.value })} placeholder={`Optionnel · départ : ${dep.km_depart || "—"}`} data-testid="retour-km" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <Label>Niveau carburant</Label>
              <Select value={retour.niveau_carburant} onValueChange={(v) => setRetour({ ...retour, niveau_carburant: v })}>
                <SelectTrigger data-testid="retour-fuel"><SelectValue /></SelectTrigger>
                <SelectContent>{FUEL_LEVELS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Suppléments (CHF)</Label>
              <Input type="number" step="1" value={retour.supplements} onChange={(e) => setRetour({ ...retour, supplements: e.target.value })} data-testid="retour-supplements" />
            </div>
          </div>
          {retour.supplements > 0 && (
            <div className="mt-3">
              <Label>Motif des suppléments</Label>
              <Input value={retour.supplements_motif} onChange={(e) => setRetour({ ...retour, supplements_motif: e.target.value })} placeholder="Carburant manquant, dommages…" data-testid="retour-supp-motif" />
            </div>
          )}
          <div className="mt-3">
            <Label>Nouveaux dommages constatés</Label>
            <Textarea value={retour.nouveaux_dommages} onChange={(e) => setRetour({ ...retour, nouveaux_dommages: e.target.value })} rows={2} placeholder="Aucun, ou décrire les nouveaux dommages…" data-testid="retour-dommages" />
          </div>
          <div className="mt-3">
            <Label>Remarques</Label>
            <Textarea value={retour.etat_final} onChange={(e) => setRetour({ ...retour, etat_final: e.target.value })} rows={2} data-testid="retour-remarques" />
          </div>
          <div className="mt-4">
            <Label className="text-xs uppercase text-gray-500 font-semibold mb-2 block">Photos des 4 coins au retour</Label>
            <PhotosCorners photos={retour.photos} onChange={(p) => setRetour({ ...retour, photos: p })} testid="retour-photos" />
          </div>
          <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-[#52a675]/20">
            <Button variant="outline" onClick={() => setShowRetour(false)}>Annuler</Button>
            <Button onClick={submitRetour} disabled={submitting} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="retour-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Clôturer le prêt"}
            </Button>
          </div>
        </Card>
      )}

      <Tabs defaultValue="depose">
        <TabsList>
          <TabsTrigger value="depose" data-testid="tab-pret-depose">Dépose</TabsTrigger>
          {ret && <TabsTrigger value="retour" data-testid="tab-pret-retour">Retour</TabsTrigger>}
          <TabsTrigger value="permis" data-testid="tab-pret-permis">Permis & identité</TabsTrigger>
        </TabsList>
        <TabsContent value="depose" className="mt-4">
          <Card className="p-5 border-gray-200">
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <Field label="Date/heure dépose" value={dep.date_heure?.slice(0, 16).replace("T", " ")} />
              <Field label="Retour prévu" value={dep.date_retour_prevu?.slice(0, 16).replace("T", " ")} />
              <Field label="Km au départ" value={`${dep.km_depart?.toLocaleString("fr-CH")} km`} />
              <Field label="Niveau carburant" value={dep.niveau_carburant} />
              <Field label="Tarif" value={dep.courtoisie ? "Courtoisie (0 CHF)" : `${dep.tarif_jour?.toFixed(2)} CHF/jour`} />
              <Field label="Caution" value={dep.caution_montant > 0 ? `${dep.caution_montant.toFixed(2)} CHF (${dep.caution_mode})` : "Aucune"} />
            </div>
            {dep.etat_initial && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="text-xs uppercase font-semibold text-gray-500 mb-1">Dommages préexistants</div>
                <div className="text-sm bg-amber-50 border border-amber-200 rounded p-2">{dep.etat_initial}</div>
              </div>
            )}
            {dep.photos?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="text-xs uppercase font-semibold text-gray-500 mb-2">Photos dépose ({dep.photos.length})</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {dep.photos.map((p, i) => (
                    <img key={i} src={p} alt={`Photo ${i + 1}`} className="w-full h-28 object-cover rounded border border-gray-200" />
                  ))}
                </div>
              </div>
            )}
          </Card>
        </TabsContent>
        {ret && (
          <TabsContent value="retour" className="mt-4">
            <Card className="p-5 border-gray-200">
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <Field label="Date/heure retour" value={ret.date_heure?.slice(0, 16).replace("T", " ")} />
                <Field label="Km retour" value={`${ret.km_retour?.toLocaleString("fr-CH")} km`} />
                <Field label="Écart km" value={`${ret.ecart_km?.toLocaleString("fr-CH")} km`} />
                <Field label="Durée" value={`${ret.duree_jours} jour(s)`} />
                <Field label="Niveau carburant" value={ret.niveau_carburant} />
                <Field label="Total facturé" value={`${ret.total_facture?.toFixed(2)} CHF`} highlight />
              </div>
              {ret.nouveaux_dommages && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="text-xs uppercase font-semibold text-gray-500 mb-1 flex items-center gap-1">
                    <AlertTriangle className="w-3.5 h-3.5 text-orange-600" /> Nouveaux dommages
                  </div>
                  <div className="text-sm bg-orange-50 border border-orange-200 rounded p-2">{ret.nouveaux_dommages}</div>
                </div>
              )}
            </Card>
          </TabsContent>
        )}
        <TabsContent value="permis" className="mt-4">
          <Card className="p-5 border-gray-200">
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <Field label="N° permis" value={cond.permis_numero} mono />
              <Field label="Catégorie" value={cond.permis_categorie} />
              <Field label="Délivrance" value={cond.permis_date_delivrance} />
              <Field label="Pièce d'identité" value={cond.piece_identite_type} />
              <Field label="N° pièce" value={cond.piece_identite_numero} mono />
              <Field label="Email" value={cond.email} />
              <Field label="Adresse" value={cond.adresse} />
            </div>
            <PermisPhotosEditor pid={id} cond={cond} onSaved={fetch1} />
          </Card>
        </TabsContent>
      </Tabs>

      {user?.role === "admin" && (
        <div className="mt-4">
          <Button variant="outline" onClick={removePret} className="text-red-600 hover:bg-red-50" data-testid="pret-delete">
            <Trash2 className="w-4 h-4 mr-2" /> Supprimer ce prêt
          </Button>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, mono, highlight }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</div>
      <div className={`text-sm ${mono ? "font-mono" : ""} ${highlight ? "font-bold text-[#2d5f3f] text-base" : "text-gray-900"}`}>
        {value || <span className="text-gray-300 italic">—</span>}
      </div>
    </div>
  );
}

/**
 * iter56 — Éditeur de photos permis sur un prêt EXISTANT.
 * Use case : la photo a été prise au téléphone au moment de la remise mais
 * pas uploadée. Le user vient l'ajouter plus tard depuis le bureau, sans
 * devoir recréer ou modifier tout le prêt.
 */
function PermisPhotosEditor({ pid, cond, onSaved }) {
  const [recto, setRecto] = useState(cond?.permis_photo_recto || null);
  const [verso, setVerso] = useState(cond?.permis_photo_verso || null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setRecto(cond?.permis_photo_recto || null);
    setVerso(cond?.permis_photo_verso || null);
  }, [cond?.permis_photo_recto, cond?.permis_photo_verso]);

  const initialRecto = cond?.permis_photo_recto || null;
  const initialVerso = cond?.permis_photo_verso || null;
  const dirty = recto !== initialRecto || verso !== initialVerso;

  const handleSave = async () => {
    setSaving(true);
    try {
      // null = champ inchangé. "" = effacement explicite. Sinon string = nouvelle valeur.
      const payload = {};
      if (recto !== initialRecto) payload.permis_photo_recto = recto || "";
      if (verso !== initialVerso) payload.permis_photo_verso = verso || "";
      await api.patch(`/prets/${pid}/permis-photos`, payload);
      toast.success("Photos permis enregistrées");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'enregistrement");
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-4 pt-4 border-t border-gray-100">
      {!recto && !verso && (
        <div
          className="mb-3 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded p-2.5 flex items-start gap-2"
          data-testid="pret-permis-missing-warning"
        >
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            <b>Permis de conduire non scanné.</b> Ajoutez-le ci-dessous — depuis l&apos;appareil photo
            ou la galerie du téléphone. C&apos;est possible pendant la location comme après la clôture du dossier.
          </span>
        </div>
      )}
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="text-xs text-gray-500">
          💡 Photo prise au téléphone à la dépose ? Ajoutez-la plus tard (appareil photo ou galerie),
          même sur un prêt déjà clôturé, sans toucher au reste du dossier.
        </div>
        {dirty && (
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving}
            className="bg-[#52a675] hover:bg-[#3d8a5c]"
            data-testid="pret-permis-photos-save"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            Enregistrer les photos
          </Button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-xs uppercase text-gray-500 font-semibold">Permis - recto</Label>
          <div className="mt-1">
            <PhotoCornerSlot
              label="Permis - recto"
              value={recto}
              onChange={setRecto}
              testid="pret-detail-permis-recto"
            />
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase text-gray-500 font-semibold">Permis - verso</Label>
          <div className="mt-1">
            <PhotoCornerSlot
              label="Permis - verso"
              value={verso}
              onChange={setVerso}
              testid="pret-detail-permis-verso"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

